import { CSSObject, keyframes } from '@emotion/react';
import { Theme, alpha } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import {
  FadeBorderColorParams,
  HighlightCellParams,
  RachelAddedRowsStylesParams,
} from './rachelEffects.styles.types';

export const RACHEL_EFFECT_MS = {
  rowEnter: 400,
  rowExit: 450,
  highlight: 5000,
  valueChange: 1600,
};

const HIGHLIGHT_HOLD_PERCENT = 70;
const BORDER_OPACITY = 0.4;
const SURFACE_OPACITY = 0.04;
const CELL_SURFACE_MIX_PERCENT = 8;
const ROW_SLIDE_PX = 16;
const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

const withoutMotion = (style: CSSObject): CSSObject => ({
  ...style,
  [REDUCED_MOTION]: { animation: 'none' },
});

const highlightRow = (color: string) => keyframes`
  0%, ${HIGHLIGHT_HOLD_PERCENT}% {
    box-shadow: inset 0 0 0 1px ${alpha(color, BORDER_OPACITY)};
    background-color: ${alpha(color, SURFACE_OPACITY)};
  }
  100% {
    box-shadow: inset 0 0 0 1px ${alpha(color, 0)};
    background-color: transparent;
  }
`;

const highlightCell = ({ color, surface }: HighlightCellParams) => keyframes`
  0%, ${HIGHLIGHT_HOLD_PERCENT}% {
    background-color: color-mix(in srgb, ${color} ${CELL_SURFACE_MIX_PERCENT}%, ${surface});
  }
`;

const enterFromLeft = keyframes`
  0% { opacity: 0; transform: translateX(-${ROW_SLIDE_PX}px); }
  100% { opacity: 1; transform: none; }
`;

const exitToLeft = (color: string) => keyframes`
  0% {
    opacity: 1;
    transform: none;
    box-shadow: inset 0 0 0 1px ${alpha(color, BORDER_OPACITY)};
    background-color: ${alpha(color, SURFACE_OPACITY)};
  }
  100% {
    opacity: 0;
    transform: translateX(-${ROW_SLIDE_PX * 2}px);
    box-shadow: inset 0 0 0 1px ${alpha(color, BORDER_OPACITY)};
    background-color: ${alpha(color, SURFACE_OPACITY)};
  }
`;

const fadeColor = (color: string) => keyframes`
  0%, 30% { color: ${color}; }
`;

const fadeBorderColor = ({ from, to }: FadeBorderColorParams) => keyframes`
  0%, 50% { border-color: ${from}; }
  100% { border-color: ${to}; }
`;

const fadeSurface = (color: string) => keyframes`
  0%, 50% { background-color: ${alpha(color, SURFACE_OPACITY * 2)}; }
  100% { background-color: transparent; }
`;

const added = (theme: Theme): CSSObject =>
  withoutMotion({
    borderRadius: theme.shape.borderRadius,
    animation: `${enterFromLeft} ${RACHEL_EFFECT_MS.rowEnter}ms ease-out both, ${highlightRow(theme.palette.primary.main)} ${RACHEL_EFFECT_MS.highlight}ms ease-out both`,
  });

const removed = (theme: Theme): CSSObject => ({
  borderRadius: theme.shape.borderRadius,
  pointerEvents: 'none',
  animation: `${exitToLeft(theme.palette.error.main)} ${RACHEL_EFFECT_MS.rowExit}ms ease-in both`,
  [REDUCED_MOTION]: { animation: 'none', opacity: 0 },
});

const addedCell = (theme: Theme): CSSObject =>
  withoutMotion({
    animation: `${highlightCell({ color: theme.palette.primary.main, surface: theme.palette.background.paper })} ${RACHEL_EFFECT_MS.highlight}ms ease-out`,
  });

const changedText = (theme: Theme): CSSObject =>
  withoutMotion({
    animation: `${fadeColor(theme.palette.primary.main)} ${RACHEL_EFFECT_MS.valueChange}ms ease-out`,
  });

const changedInput = (theme: Theme): CSSObject => ({
  '& .MuiOutlinedInput-notchedOutline': {
    animation: `${fadeBorderColor({ from: alpha(theme.palette.primary.main, BORDER_OPACITY * 2), to: theme.palette.custom.border.primary })} ${RACHEL_EFFECT_MS.valueChange}ms ease-out`,
  },
  '& input': {
    animation: `${fadeColor(theme.palette.primary.main)} ${RACHEL_EFFECT_MS.valueChange}ms ease-out`,
  },
  [REDUCED_MOTION]: {
    '& .MuiOutlinedInput-notchedOutline, & input': { animation: 'none' },
  },
});

const changedField = (theme: Theme): CSSObject =>
  withoutMotion({
    borderRadius: theme.shape.borderRadius,
    animation: `${fadeSurface(theme.palette.primary.main)} ${RACHEL_EFFECT_MS.valueChange}ms ease-out`,
  });

export const useRachelEffectStyles = makeStyles()((theme) => ({
  added: added(theme),
  removed: removed(theme),
  changedText: changedText(theme),
  changedInput: changedInput(theme),
  changedField: changedField(theme),
}));

export const useRachelAddedRowsStyles = makeStyles<RachelAddedRowsStylesParams>()(
  (theme, { rowIds }) => ({
    rows: {
      display: 'contents',
      ...(rowIds.length
        ? { [rowIds.map((rowId) => `[data-row-id="${rowId}"] > td`).join(', ')]: addedCell(theme) }
        : {}),
    },
  }),
);
